import React, { useEffect, useState } from 'react';
import { Box, Loader, Modal, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlayerPlayFilled,
  IconCheck,
  IconX,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useOnboardingStyles } from './onboardingStyles';
import { useOnbColors } from './onboardingColors';
import styles from './Onboarding.module.css';

// Single notification shape used across the whole onboarding flow
type NotifyType = 'success' | 'error' | 'warning';

export function notify(type: NotifyType, message: string, title?: string) {
  const config = {
    success: { color: 'green', title: 'Success', icon: <IconCheck size="1.1rem" /> },
    error: { color: 'red', title: 'Error', icon: <IconX size="1.1rem" /> },
    warning: { color: 'orange', title: 'Warning', icon: <IconAlertTriangle size="1.1rem" /> },
  }[type];

  notifications.show({
    color: config.color,
    icon: config.icon,
    title: title || config.title,
    message,
  });
}

// Cookies consumed by backend_api/shopify_dashboard.php after the Shopify OAuth round-trip:
// it connects the store to this website (main) or related domain (sub), then sends the
// merchant back to `returnPath`. Returns false when the install URL is not configured.
export function startShopifyConnect(domainId: string, domainType: 'main' | 'sub', returnPath: string): boolean {
  const installUrl = process.env.REACT_APP_SHOPIFY_INSTALL_URL;
  if (!installUrl) {
    notify('error', 'Shopify install URL is not configured.', 'Cannot connect');
    return false;
  }
  try {
    const apiBaseUrl = process.env.REACT_APP_BACKEND_API_BASE_URL || '';
    const hostname = new URL(apiBaseUrl).hostname;
    const cookieDomain = '.' + hostname.split('.').slice(-2).join('.');
    const opts = `domain=${cookieDomain}; path=/; max-age=3600; Secure; SameSite=None`;
    document.cookie = `shopifyConnectDomainId=${domainId}; ${opts}`;
    document.cookie = `shopifyDomainType=${domainType}; ${opts}`;
    document.cookie = `shopifyReturnPath=${returnPath}; ${opts}`;
  } catch {
    notify('error', 'Could not start the Shopify connection. Please try again.', 'Cannot connect');
    return false;
  }
  const win = window.open(installUrl, '_blank');
  if (!win) {
    notify('error', 'Please allow pop-ups for this site, then click Connect Shopify Store again.', 'Pop-up blocked');
    return false;
  }
  // Sever the opener link so the Shopify tab cannot script this one
  try { win.opener = null; } catch { /* ignore */ }
  return true;
}

// Resolve a URL to an embeddable player: Vimeo/YouTube → iframe, otherwise a direct file
function resolveVideo(url: string): { type: 'iframe' | 'file'; src: string } {
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i) || url.match(/player\.vimeo\.com\/video\/(\d+)/i);
  if (vimeo) return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&title=0&byline=0&portrait=0` };
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/i);
  if (yt) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1` };
  return { type: 'file', src: url };
}

// Format seconds → m:ss
const fmtDuration = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Reusable "watch how this works" video card + player modal, shown across steps
export function StepVideo({
  duration = '0:45',
  title = 'Watch how this works',
  sublabel = 'A 45-second walkthrough of this step',
  modalTitle,
  videoUrl = '',
  variant = 'card',
  badge,
}: {
  duration?: string;
  title?: string;
  sublabel?: string;
  modalTitle?: string;
  videoUrl?: string;
  variant?: 'card' | 'hero';
  badge?: string;
}) {
  const { classes } = useOnboardingStyles();
  const [opened, setOpened] = useState(false);
  const [detected, setDetected] = useState<string | null>(null);
  const embed = resolveVideo(videoUrl);

  // Detect the real duration: Vimeo via oEmbed, direct files via metadata
  useEffect(() => {
    let cancelled = false;
    setDetected(null);
    const apply = (secs: number) => {
      if (!cancelled && secs > 0) setDetected(fmtDuration(secs));
    };
    if (!videoUrl) return;
    const vimeo = videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (vimeo) {
      fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${vimeo[1]}`)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d?.duration && apply(d.duration))
        .catch(() => {});
    } else if (embed.type === 'file') {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => apply(v.duration);
      v.src = embed.src;
    }
    return () => {
      cancelled = true;
    };
  }, [videoUrl]); // eslint-disable-line

  const shown = detected || duration;

  // No real walkthrough recorded for this step yet — render nothing rather than a placeholder
  if (!videoUrl) return null;

  return (
    <>
      {variant === 'hero' ? (
        <div className={classes.heroVideo} onClick={() => setOpened(true)}>
          <span className={classes.heroBadge}>{`${badge || 'Welcome'} · ${shown}`}</span>
          <div className={classes.heroPlay}>
            <IconPlayerPlayFilled size={26} />
          </div>
          <div className={classes.heroOverlay}>
            <div className={classes.heroTitle}>{title}</div>
            <div className={classes.heroSub}>{sublabel}</div>
          </div>
        </div>
      ) : (
        <button type="button" className={classes.videoCard} onClick={() => setOpened(true)}>
          <div className={classes.videoThumb}>
            <div className={classes.videoBars}>
              <div className={classes.videoBar} style={{ width: '70%' }} />
              <div className={classes.videoBar} style={{ width: '45%' }} />
            </div>
            <div className={classes.videoPlay}>
              <IconPlayerPlayFilled size={15} />
            </div>
            <span className={classes.videoDuration}>{shown}</span>
          </div>
          <div>
            <div className={classes.videoTitle}>{title}</div>
            <div className={classes.videoSub}>{sublabel}</div>
          </div>
        </button>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<Text fw={700} fz="md">{modalTitle || title}</Text>}
        size={760}
        centered
        radius="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
        styles={{ body: { padding: 0 }, header: { padding: '16px 20px', marginBottom: 0 } }}
      >
        <div className={classes.videoFrame}>
          {embed.type === 'iframe' ? (
            <iframe
              className={classes.videoPlayer}
              src={embed.src}
              title={modalTitle || title}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video className={classes.videoPlayer} src={embed.src} controls playsInline preload="metadata" />
          )}
        </div>
      </Modal>
    </>
  );
}

// Animated green success checkmark used across steps
export function SuccessCheck({ size = 76 }: { size?: number }) {
  const col = useOnbColors();
  return (
    <div
      className={`${styles.popIn} ${styles.successRing}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: col.dark ? '#3A4150' : '#111827',
        display: 'grid',
        placeItems: 'center',
        boxShadow: '0 14px 30px -12px rgba(17,24,39,0.5)',
      }}
    >
      <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 32 32" fill="none">
        <path
          className={styles.checkPath}
          d="M7 16.5L13.5 23L25 9"
          stroke="#fff"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// Setup-step screenshot with a loading spinner while it downloads.
// Hides itself (renders nothing) if the image is missing/fails, matching the old onError behavior.
// Pass a stable `key={src}` from the caller so it resets when the step changes.
export function StepImage({
  src,
  alt,
  borderColor,
  radius = 12,
}: {
  src: string;
  alt: string;
  borderColor?: string;
  radius?: number;
}) {
  const col = useOnbColors();
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const border = `1px solid ${borderColor || col.border}`;
  if (errored) return null;
  return (
    <Box style={{ width: '100%', marginTop: 8 }}>
      {loading && (
        <Box
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 140,
            borderRadius: radius,
            border,
            background: col.surface,
          }}
        >
          <Loader size="sm" color="grape" />
        </Box>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoading(false)}
        onError={() => setErrored(true)}
        style={{
          display: loading ? 'none' : 'block',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: radius,
          border,
        }}
      />
    </Box>
  );
}

// Down-arrow connector for vertical journey illustrations
export function FlowArrowDown() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', padding: '2px 0' }}>
      <svg width="20" height="26" viewBox="0 0 20 26" fill="none">
        <path d="M10 1V21" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 16L10 23L16 16" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
